/**
 * Ofis çalışma alanı (katalog s. 21–23).
 *
 *   Clients        : bütün müşteriler tek bakışta — belge x/y, incelenecek, durum, sorumlu
 *   Inbox          : incelenmemiş belgeler (bütün müşteriler)
 *   Missing        : açık taleplerde eksik belgeler
 *   Month close    : kapanışa hazırlık listesi
 *   Müşteri        : Inbox · Missing · Entries · Month close sekmeleri
 *
 * Veri: firm_clients_overview, firm_client_detail, set_document_review,
 * create_document_request, cancel_document_request, set_client_assignment,
 * transactions (RLS: firm_linked_profile_ids). Burada güvenlik yok; her karar
 * veritabanında.
 *
 * Extract: belge Worker'dan 60 sn imzalı adresle indirilir, AI sayfasının
 * Document sekmesinde okunur; "Import" müşterinin defterine yazar (fwImportRows).
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import { KIND_LABEL } from '../doc-requests/texts.ts';

type Mode = 'clients' | 'inbox' | 'missing' | 'close' | 'client';
type Tab = 'inbox' | 'missing' | 'entries' | 'close';

interface ClientRow {
  link_id: string; client_owner_id: string; client_name: string; contact_name: string; linked_at: string;
  assigned: Array<{ member_id: string; name: string }>; mine: boolean;
  docs_requested: number; docs_received: number; to_review: number;
  first_missing: { kind: string; label: string; reminders: number; due_date: string | null } | null;
  status: 'waiting' | 'review' | 'ready' | 'closed'; last_activity_at: string | null;
}
interface Overview { ok: boolean; error?: string; role: string; firm_name: string; clients: ClientRow[];
  summary: { total: number; closed?: number; waiting: number; review: number; ready: number; inbox: number; missing: number } }
interface Doc { id: string; name: string; file_type: string; size_bytes: number; created_at: string; has_file: boolean;
  review_status: 'new' | 'extracted' | 'no_entries'; entries: number; request_kind: string | null;
  request_title: string | null; source: 'client' | 'firm' | 'link' }
interface ReqItem { id: string; kind: string; label: string; status: string; received_at: string | null }
interface Req { id: string; title: string; status: string; period: string; due_date: string | null; channels: string[];
  reminder_count: number; created_at: string; items: ReqItem[]; events: Array<{ kind: string; at: string }> }
interface Detail { ok: boolean; error?: string; link_id: string; client_owner_id: string; profile_id: string;
  client_name: string; contact_name: string; linked_at: string; assigned: Array<{ member_id: string; name: string }>;
  documents: Doc[]; requests: Req[] }
interface Entry { id: string; date: string; type: string; category: string | null; amount: number; currency: string | null; note: string | null }

let mode: Mode = 'clients';
let tab: Tab = 'inbox';
let linkId: string | null = null;
let overview: Overview | null = null;
let detail: Detail | null = null;
let filter: 'all' | 'mine' | 'waiting' | 'ready' = 'all';
let search = '';
let formOpen = false;
let assignOpen = false;
/* Gelen kutusunda açık okuma (katalog s. 23): belge kimliği ve okuma panelinin eski yeri. */
let extractFor: string | null = null;
let panelHome: { parent: Node; next: Node | null } | null = null;

const PANEL_IDS = ['pdf-status', 'pdf-results'];

/** Okuma paneli (#pdf-status, #pdf-results) Belge sekmesinden gelen kutusuna taşınır.
 *  Kimlikler tek kalır; sayfa yeniden çizilmeden önce eski yerine döner. */
function mountPanel(host: HTMLElement): void {
  const first = el(PANEL_IDS[0]);
  if (!first || !first.parentNode) return;
  if (!panelHome) panelHome = { parent: first.parentNode, next: el(PANEL_IDS[1])?.nextSibling ?? null };
  for (const id of PANEL_IDS) { const n = el(id); if (n) host.appendChild(n); }
}

function restorePanel(): void {
  if (!panelHome) return;
  for (const id of PANEL_IDS) {
    const n = el(id);
    if (n) panelHome.parent.insertBefore(n, panelHome.next);
  }
  panelHome = null;
}

const NAV: Record<Mode, string> = { clients: 'ni12', inbox: 'ni13', missing: 'ni14', close: 'ni15', client: 'ni12' };
const STATUS_LABEL: Record<string, string> = { waiting: 'Waiting', review: 'In review', ready: 'Ready to close', closed: 'Closed' };

/* ── Küçük yardımcılar ─────────────────────────────────────────────── */

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
function sb(): any { return legacy.sb; }
function role(): string { return overview?.role ?? ((globalThis as any).navFirmContext?.()?.role ?? 'owner'); }
function canManage(): boolean { return role() === 'owner' || role() === 'manager'; }
function date(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}
function ago(iso: string | null | undefined): string {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + ' min ago';
  if (s < 86400) return Math.floor(s / 3600) + ' h ago';
  return Math.floor(s / 86400) + ' d ago';
}
function initial(name: string): string { return esc((name || '?').trim().charAt(0).toUpperCase()); }
function chip(text: string, tone: 'red' | 'grn' | 'amb' | 'mut'): string {
  const c = { red: 'var(--red)', grn: 'var(--grn)', amb: '#D4A017', mut: 'var(--t2)' }[tone];
  return `<span style="font-size:10px;font-weight:700;color:${c};border:1px solid ${c}55;border-radius:10px;padding:1px 8px;white-space:nowrap">${esc(text)}</span>`;
}
function toneFor(status: string): 'red' | 'grn' | 'amb' | 'mut' {
  return status === 'waiting' ? 'amb' : status === 'review' ? 'red' : status === 'closed' ? 'mut' : 'grn';
}
async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T | null> {
  try {
    const r = await sb().rpc(fn, args);
    if (r.error) { legacy.toast(String(r.error.message ?? r.error)); return null; }
    return r.data as T;
  } catch { return null; }
}
function month(): string { return new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); }

/* ── Yükleme ───────────────────────────────────────────────────────── */

async function loadOverview(): Promise<void> {
  overview = await rpc<Overview>('firm_clients_overview');
}
async function loadDetail(): Promise<void> {
  detail = linkId ? await rpc<Detail>('firm_client_detail', { p_link_id: linkId }) : null;
}

/* ── Clients / Inbox / Missing / Close listeleri ───────────────────── */

function tiles(o: Overview): string {
  const t = (label: string, n: number, tone: string) =>
    `<div class="stc"><div class="sl">${esc(label)}</div><div class="sv" translate="no" style="font-size:22px;color:${tone}">${n}</div></div>`;
  return `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:8px;margin-bottom:14px">`
    + t(month() + ' · clients', o.summary.total, 'var(--txt)')
    + t('Closed', o.summary.closed ?? 0, 'var(--grn)')
    + t('Waiting for documents', o.summary.waiting, '#D4A017')
    + t('In review', o.summary.review, 'var(--red)') + '</div>';
}

function visibleClients(o: Overview): ClientRow[] {
  const q = search.trim().toLowerCase();
  return o.clients.filter((c) =>
    (filter === 'all' || (filter === 'mine' && c.mine) || (filter === 'waiting' && c.status === 'waiting')
      || (filter === 'ready' && c.status === 'ready'))
    && (!q || c.client_name.toLowerCase().includes(q) || c.contact_name.toLowerCase().includes(q)));
}

function clientRow(c: ClientRow): string {
  const who = c.assigned.length
    ? c.assigned.map((a) => `<span class="av" title="${esc(a.name)}" style="width:22px;height:22px;font-size:9px">${initial(a.name)}</span>`).join('')
      + `<span style="font-size:11px;color:var(--t2)">${esc(c.assigned[0].name)}${c.assigned.length > 1 ? ' +' + (c.assigned.length - 1) : ''}</span>`
    : '<span style="font-size:11px;color:var(--t3)">Unassigned</span>';
  const docs = c.docs_requested
    ? `<b translate="no">${c.docs_received}/${c.docs_requested}</b>`
      + (c.first_missing ? `<div style="font-size:10px;color:var(--t2)">${esc(KIND_LABEL[c.first_missing.kind] ?? '')}${c.first_missing.label ? ' — ' + esc(c.first_missing.label) : ''} missing${c.first_missing.reminders ? ' · reminded' : ''}</div>` : '')
    : '<span style="color:var(--t3)">—</span>';
  return `<div class="card" style="margin-bottom:8px;display:flex;flex-wrap:wrap;gap:10px 16px;align-items:center">`
    + `<div style="flex:2 1 180px;min-width:0"><div style="font-weight:600">${esc(c.client_name)}</div>`
    + `<div style="font-size:10px;color:var(--t2)">${esc(c.contact_name)} · since <span translate="no">${esc(date(c.linked_at))}</span></div></div>`
    + `<div style="flex:1 1 120px;display:flex;align-items:center;gap:6px">${who}</div>`
    + `<div style="flex:1 1 130px;font-size:12px">${docs}</div>`
    + `<div style="flex:0 0 70px;font-size:12px" title="To review"><span translate="no">${c.to_review}</span> <span style="font-size:10px;color:var(--t2)">to review</span></div>`
    + `<div style="flex:0 0 100px">${chip(STATUS_LABEL[c.status], toneFor(c.status))}</div>`
    + `<div style="flex:1 1 90px;font-size:10px;color:var(--t2)">${esc(ago(c.last_activity_at))}</div>`
    + `<button class="btn bg bsm" onclick="fwNav('client','${esc(c.link_id)}')">Open</button></div>`;
}

function renderClients(root: HTMLElement): void {
  const o = overview;
  if (!o || !o.ok) { root.innerHTML = `<div class="emp">${esc(o?.error ?? 'Clients could not be loaded.')}</div>`; return; }
  const f = (id: typeof filter, label: string) =>
    `<button class="fc${filter === id ? ' on' : ''}" onclick="fwFilter('${id}')">${esc(label)}</button>`;
  const rows = visibleClients(o);
  root.innerHTML = `<div class="pt">${role() === 'staff' ? 'My clients' : 'Clients'}</div>`
    + `<div class="ps">Which client owes which document, and what can be closed today.</div>`
    + tiles(o)
    + `<div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-bottom:12px">`
    + f('all', 'All') + f('mine', 'Mine') + f('waiting', 'Waiting documents') + f('ready', 'Ready to close')
    + `<input class="inp" style="flex:1 1 160px;max-width:260px;padding:6px 10px;font-size:12px" placeholder="Search clients…" value="${esc(search)}" oninput="fwSearch(this.value)">`
    + (canManage() ? `<button class="btn br bsm" onclick="fwToggleLink()">+ Link a Client</button>` : '')
    + `</div>`
    + (o.clients.length
      ? (rows.length ? rows.map(clientRow).join('') : '<div class="emp">No clients match this filter.</div>')
      : `<div class="emp">No clients yet.${canManage() ? '<br>Use “+ Link a Client” to generate a code for your first client.' : '<br>Your manager assigns clients to you.'}</div>`);
}

async function renderInbox(root: HTMLElement): Promise<void> {
  const o = overview;
  if (!o || !o.ok) { renderClients(root); return; }
  const withDocs = o.clients.filter((c) => c.to_review > 0);
  root.innerHTML = `<div class="pt">Inbox</div><div class="ps">Documents your clients sent that nobody has reviewed yet.</div><div id="fw-inbox">…</div>`;
  const parts: string[] = [];
  for (const c of withDocs) {
    const d = await rpc<Detail>('firm_client_detail', { p_link_id: c.link_id });
    if (!d || !d.ok) continue;
    for (const doc of d.documents.filter((x) => x.review_status === 'new')) {
      parts.push(docRow(doc, d, true));
    }
  }
  const box = el('fw-inbox');
  if (box) box.innerHTML = parts.length ? parts.join('') : '<div class="emp">Nothing to review. 🎉</div>';
}

function renderMissing(root: HTMLElement): void {
  const o = overview;
  if (!o || !o.ok) { renderClients(root); return; }
  const rows = o.clients.filter((c) => c.docs_requested > c.docs_received);
  root.innerHTML = `<div class="pt">Missing documents</div><div class="ps">Open requests that are still waiting for the client. Reminders go out on day 3 and day 7.</div>`
    + (rows.length ? rows.map((c) =>
      `<div class="card" style="margin-bottom:8px;display:flex;flex-wrap:wrap;gap:8px 16px;align-items:center">`
      + `<div style="flex:2 1 180px"><div style="font-weight:600">${esc(c.client_name)}</div>`
      + `<div style="font-size:11px;color:var(--t2)">${c.first_missing ? esc(KIND_LABEL[c.first_missing.kind] ?? '') + (c.first_missing.label ? ' — ' + esc(c.first_missing.label) : '') : ''}`
      + `${c.first_missing?.due_date ? ' · due <span translate="no">' + esc(c.first_missing.due_date) + '</span>' : ''}</div></div>`
      + `<div style="flex:0 0 80px"><b translate="no">${c.docs_received}/${c.docs_requested}</b></div>`
      + `<div style="flex:0 0 110px">${c.first_missing?.reminders ? chip(c.first_missing.reminders + ' reminder(s) sent', 'amb') : chip('Not reminded yet', 'mut')}</div>`
      + `<button class="btn bg bsm" onclick="fwNav('client','${esc(c.link_id)}','missing')">Open</button></div>`).join('')
      : '<div class="emp">No missing documents.</div>');
}

function renderClose(root: HTMLElement): void {
  const o = overview;
  if (!o || !o.ok) { renderClients(root); return; }
  root.innerHTML = `<div class="pt">Month close</div><div class="ps">${esc(month())} · a client is ready when every requested document arrived and everything received was reviewed.</div>`
    + (o.clients.length ? o.clients.map((c) =>
      `<div class="card" style="margin-bottom:8px;display:flex;flex-wrap:wrap;gap:8px 16px;align-items:center">`
      + `<div style="flex:2 1 180px;font-weight:600">${esc(c.client_name)}</div>`
      + `<div style="flex:1 1 150px;font-size:11px">${c.docs_requested > c.docs_received ? '✗' : '✓'} Documents complete</div>`
      + `<div style="flex:1 1 150px;font-size:11px">${c.to_review ? '✗' : '✓'} Inbox reviewed</div>`
      + `<div style="flex:0 0 100px">${chip(STATUS_LABEL[c.status], toneFor(c.status))}</div>`
      + `<button class="btn bg bsm" onclick="fwNav('client','${esc(c.link_id)}','close')">Open</button></div>`).join('')
      : '<div class="emp">No clients yet.</div>');
}

/* ── Müşteri çalışma alanı ─────────────────────────────────────────── */

function docRow(doc: Doc, d: Detail, showClient: boolean): string {
  const icon = /jpe?g|png|webp/.test(doc.file_type) ? '🧾' : doc.file_type === 'xml' ? '📑' : '📄';
  const src = doc.request_title ? `via request “${esc(doc.request_title)}”` : doc.source === 'client' ? `uploaded by ${esc(d.contact_name || 'client')}`
    : doc.source === 'link' ? 'uploaded by email link' : 'added by your firm';
  const status = doc.review_status === 'extracted' ? chip(`Extracted · ${doc.entries}`, 'grn')
    : doc.review_status === 'no_entries' ? chip('No entries', 'mut') : chip('New', 'red');
  const kind = doc.request_kind ? esc(KIND_LABEL[doc.request_kind] ?? '') + ' · ' : '';
  return `<div class="card" style="margin-bottom:8px;display:flex;flex-wrap:wrap;gap:8px 12px;align-items:center">`
    + `<div style="font-size:20px">${icon}</div>`
    + `<div style="flex:2 1 200px;min-width:0"><div style="font-weight:600;overflow:hidden;text-overflow:ellipsis" translate="no">${esc(doc.name)}</div>`
    + `<div style="font-size:10px;color:var(--t2)">${showClient ? esc(d.client_name) + ' · ' : ''}${kind}${src} · ${esc(ago(doc.created_at))}</div></div>`
    + `<div>${status}</div>`
    + `<div style="display:flex;gap:6px;flex-wrap:wrap">`
    + (doc.has_file ? `<button class="btn bg bsm" onclick="faOpenDocument('${esc(doc.id)}')">Open</button>` : '')
    + (doc.has_file ? `<button class="btn br bsm" onclick="fwExtract('${esc(d.link_id)}','${esc(doc.id)}')">✦ Extract</button>` : '')
    + (doc.review_status === 'new' ? `<button class="btn bg bsm" onclick="fwReview('${esc(doc.id)}','no_entries')">No entries</button>` : '')
    + (showClient ? `<button class="btn bg bsm" onclick="fwNav('client','${esc(d.link_id)}')">Client</button>` : '')
    + `</div></div>`;
}

function header(d: Detail): string {
  const t = (id: Tab, label: string, n?: number) =>
    `<button class="tb2${tab === id ? ' on' : ''}" onclick="fwTab('${id}')">${esc(label)}${n ? ' · <span translate="no">' + n + '</span>' : ''}</button>`;
  const missing = d.requests.filter((r) => r.status === 'open').reduce((s, r) => s + r.items.filter((i) => i.status === 'missing').length, 0);
  const inbox = d.documents.filter((x) => x.review_status === 'new').length;
  return `<div style="margin-bottom:10px"><button class="btn bg bsm" onclick="fwNav('clients')">← Clients</button></div>`
    + `<div class="card" style="margin-bottom:12px;display:flex;align-items:center;gap:12px;flex-wrap:wrap">`
    + `<div class="av" style="width:40px;height:40px;font-size:14px">${initial(d.client_name)}</div>`
    + `<div style="flex:1;min-width:0"><div style="font-size:16px;font-weight:700">${esc(d.client_name)}</div>`
    + `<div style="font-size:11px;color:var(--t2)">${esc(d.contact_name)} · linked <span translate="no">${esc(date(d.linked_at))}</span>`
    + ` · ${d.assigned.length ? 'assigned to ' + d.assigned.map((a) => esc(a.name)).join(', ') : 'unassigned'}</div></div>`
    + (canManage() ? `<button class="btn bg bsm" onclick="fwAssign()">Assign</button>` : '')
    + `</div><div id="fw-assign"></div>`
    + `<div class="tr">${t('inbox', '📥 Inbox', inbox)}${t('missing', '⚠ Missing', missing)}${t('entries', '📒 Entries')}${t('close', '✓ Month close')}</div>`;
}

function requestCard(r: Req): string {
  const got = r.items.filter((i) => i.status === 'received').length;
  const sent = r.events.find((e) => e.kind === 'email_sent');
  const tl = [`Created ${date(r.created_at)}`];
  if (sent) tl.push('Email sent');
  if (r.reminder_count) tl.push(`${r.reminder_count} reminder(s)`);
  const tone = r.status === 'fulfilled' ? 'grn' : r.status === 'cancelled' ? 'mut' : 'amb';
  return `<div class="card" style="margin-bottom:8px">`
    + `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap"><div style="flex:1;font-weight:600">${esc(r.title)}`
    + `${r.period ? ' <span style="font-size:11px;color:var(--t2)" translate="no">· ' + esc(r.period) + '</span>' : ''}</div>`
    + `<b translate="no">${got}/${r.items.length}</b> ${chip(r.status === 'open' ? 'Open' : r.status === 'fulfilled' ? 'Complete' : 'Cancelled', tone)}`
    + (r.status === 'open' ? ` <button class="btn bg bsm" onclick="fwCancelRequest('${esc(r.id)}')">Cancel</button>` : '') + `</div>`
    + r.items.map((i) => `<div style="display:flex;gap:8px;font-size:12px;padding:6px 0;border-top:1px solid var(--div);margin-top:6px">`
      + `<span>${i.status === 'received' ? '✓' : '○'}</span><span style="flex:1">${esc(KIND_LABEL[i.kind] ?? '')}${i.label ? ' — ' + esc(i.label) : ''}</span>`
      + `<span style="font-size:10px;color:var(--t2)">${i.status === 'received' ? esc(date(i.received_at)) : r.due_date ? 'due <span translate="no">' + esc(r.due_date) + '</span>' : ''}</span></div>`).join('')
    + `<div style="font-size:10px;color:var(--t2);margin-top:6px">${tl.map(esc).join(' · ')}</div></div>`;
}

const TEMPLATES: Record<string, Array<{ kind: string; label: string }>> = {
  monthly: [{ kind: 'bank_statement', label: '' }, { kind: 'invoice_sales', label: '' }, { kind: 'invoice_purchase', label: '' }, { kind: 'receipt', label: '' }],
  quarterly: [{ kind: 'bank_statement', label: 'Month 1' }, { kind: 'bank_statement', label: 'Month 2' }, { kind: 'bank_statement', label: 'Month 3' },
    { kind: 'invoice_sales', label: 'Quarter' }, { kind: 'invoice_purchase', label: 'Quarter' }],
  year_end: [{ kind: 'bank_statement', label: 'December' }, { kind: 'invoice_sales', label: 'Year' }, { kind: 'invoice_purchase', label: 'Year' },
    { kind: 'other', label: 'Year-end balances' }],
};
let draftItems: Array<{ kind: string; label: string; on: boolean }> = TEMPLATES.monthly.map((i) => ({ ...i, on: true }));

function requestForm(d: Detail): string {
  const kinds = Object.keys(KIND_LABEL);
  const items = draftItems.map((it, i) =>
    `<div style="display:flex;gap:6px;align-items:center;margin-bottom:6px">`
    + `<input type="checkbox" ${it.on ? 'checked' : ''} onchange="fwDraft(${i},'on',this.checked)">`
    + `<select class="inp" style="flex:0 0 170px;padding:6px;font-size:12px" onchange="fwDraft(${i},'kind',this.value)">`
    + kinds.map((k) => `<option value="${k}"${k === it.kind ? ' selected' : ''}>${esc(KIND_LABEL[k])}</option>`).join('') + `</select>`
    + `<input class="inp" style="padding:6px;font-size:12px" maxlength="80" placeholder="Detail (optional)" value="${esc(it.label)}" oninput="fwDraft(${i},'label',this.value)"></div>`).join('');
  const n = draftItems.filter((x) => x.on).length;
  return `<div class="card" style="margin-bottom:12px">`
    + `<div class="set">Request documents · ${esc(d.client_name)}</div>`
    + `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px">`
    + `<button class="fc" onclick="fwTemplate('monthly')">Monthly pack</button><button class="fc" onclick="fwTemplate('quarterly')">Quarterly pack</button><button class="fc" onclick="fwTemplate('year_end')">Year end</button></div>`
    + `<div class="g2"><div class="fg"><label>Title</label><input class="inp" id="fw-rq-title" maxlength="120" value="${esc(month())} documents"></div>`
    + `<div class="fg"><label>Period</label><input class="inp" id="fw-rq-period" maxlength="20" placeholder="e.g. 2026-09"></div></div>`
    + `<div class="fg"><label>Documents</label>${items}<button class="btn bg bsm" onclick="fwDraftAdd()">+ Add an item</button></div>`
    + `<div class="g2"><div class="fg"><label>Due</label><input class="inp" type="date" id="fw-rq-due"></div>`
    + `<div class="fg"><label>Send via</label><div style="display:flex;gap:12px;flex-wrap:wrap;font-size:12px;padding-top:8px">`
    + `<label style="text-transform:none"><input type="checkbox" checked disabled> In app</label>`
    + `<label style="text-transform:none"><input type="checkbox" id="fw-rq-email" checked> Email</label>`
    + `<label style="text-transform:none;color:var(--t3)"><input type="checkbox" disabled> SMS · soon</label></div></div></div>`
    + `<div class="fg"><label>Message (optional)</label><textarea class="inp" id="fw-rq-msg" rows="2" maxlength="1000"></textarea></div>`
    + `<div style="font-size:11px;color:var(--t2);margin-bottom:10px">Preview: ${esc(overview?.firm_name ?? 'Your firm')} needs ${n} document${n === 1 ? '' : 's'} from ${esc(d.client_name)}. Reminders: day 3 and day 7 by email.</div>`
    + `<div style="display:flex;gap:8px"><button class="btn br" onclick="fwSendRequest()">Send request</button><button class="btn bg" onclick="fwForm(false)">Cancel</button></div></div>`;
}

async function renderEntries(box: HTMLElement, d: Detail): Promise<void> {
  box.innerHTML = '…';
  const r = await sb().from('transactions').select('id,date,type,category,amount,currency,note')
    .eq('profile_id', d.profile_id).order('date', { ascending: false }).limit(200);
  const rows: Entry[] = (r && r.data) || [];
  const cur = rows.find((x) => x.currency)?.currency
    || ((globalThis as any).exDefaultCurrency?.() as string) || 'USD';
  box.innerHTML = `<div class="card" style="margin-bottom:12px"><div class="set">Add entry · signed with your name</div>`
    + `<div style="display:flex;gap:6px;flex-wrap:wrap">`
    + `<input class="inp" type="date" id="fw-en-date" style="flex:0 0 150px;padding:6px" value="${new Date().toISOString().slice(0, 10)}">`
    + `<select class="inp" id="fw-en-type" style="flex:0 0 110px;padding:6px"><option value="expense">Expense</option><option value="income">Income</option></select>`
    + `<input class="inp" type="number" step="0.01" min="0" id="fw-en-amount" placeholder="Amount (${esc(cur)})" style="flex:0 0 140px;padding:6px">`
    + `<input class="inp" id="fw-en-cat" placeholder="Category" style="flex:1 1 120px;padding:6px">`
    + `<input class="inp" id="fw-en-note" placeholder="Description" style="flex:2 1 160px;padding:6px">`
    + `<button class="btn br bsm" onclick="fwAddEntry('${esc(cur)}')">Add</button></div>`
    + `<div style="font-size:10px;color:var(--t2);margin-top:6px">You can add and correct entries. Deleting is not possible from the firm side.</div></div>`
    + (rows.length ? rows.map((e) =>
      `<div class="txi" id="fw-en-${esc(e.id)}"><div class="tin"><div class="tnm">${esc(e.note || e.category || '—')}</div>`
      + `<div class="tsb" translate="no">${esc(e.date)}${e.category ? ' · ' + esc(e.category) : ''}</div></div>`
      + `<div class="tam" translate="no" style="color:${e.type === 'income' ? 'var(--grn)' : 'var(--red)'}">${e.type === 'income' ? '+' : '−'}${Number(e.amount).toFixed(2)} ${esc(e.currency || cur)}</div>`
      + `<div class="tac"><button class="btn bg bsm" onclick="fwEditEntry('${esc(e.id)}')">Edit</button></div></div>`).join('')
      : '<div class="emp">No entries yet.</div>');
}

function renderCloseTab(box: HTMLElement, d: Detail): void {
  const missing = d.requests.filter((r) => r.status === 'open').reduce((s, r) => s + r.items.filter((i) => i.status === 'missing').length, 0);
  const inbox = d.documents.filter((x) => x.review_status === 'new').length;
  const line = (ok: boolean, text: string) => `<div class="tip"><span>${ok ? '✓' : '✗'}</span><span>${esc(text)}</span></div>`;
  box.innerHTML = `<div class="card"><div class="set">${esc(month())} checklist</div>`
    + line(missing === 0, missing ? `${missing} requested document(s) still missing` : 'All requested documents arrived')
    + line(inbox === 0, inbox ? `${inbox} document(s) not reviewed` : 'Every document was reviewed')
    + `<div id="fw-close-actions" style="margin-top:10px"></div></div>`;
  (globalThis as any).mcRenderClose?.(d.link_id, d.profile_id, missing === 0 && inbox === 0);
}

async function renderClient(root: HTMLElement): Promise<void> {
  restorePanel();
  await loadDetail();
  const d = detail;
  if (!d || !d.ok) { root.innerHTML = `<div class="emp">${esc(d?.error ?? 'Client not found.')}</div>`; return; }
  root.innerHTML = header(d) + `<div id="fw-tab"></div>`;
  const box = el('fw-tab');
  if (!box) return;
  if (tab === 'inbox') {
    const reading = extractFor ? d.documents.find((x) => x.id === extractFor) : undefined;
    box.innerHTML = (reading
      ? `<div class="card" id="fw-extract" style="margin-bottom:12px;border-color:var(--rB)">`
        + `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px"><b translate="no">${esc(reading.name)}</b>`
        + `<span style="font-size:11px;font-weight:700;color:var(--red)">✦ Extract</span><span style="flex:1"></span>`
        + `<button class="btn bg bsm" onclick="fwExtractClose()">Close</button></div>`
        + `<div id="fw-ex-tier" style="font-size:11px;color:var(--t2);margin-bottom:8px">Reading…</div>`
        + `<div id="fw-ex-host"></div><div id="fw-ex-dups"></div>`
        + `<div id="fw-ex-sign" style="font-size:11px;color:#7aa2ff;margin-top:8px"></div></div>`
      : '')
      + (d.documents.length ? d.documents.map((x) => docRow(x, d, false)).join('')
        : '<div class="emp">No documents yet. Send a request from the Missing tab.</div>');
    const host = el('fw-ex-host');
    if (host) mountPanel(host);
  } else if (tab === 'missing') {
    box.innerHTML = (formOpen ? requestForm(d) : `<div style="margin-bottom:10px"><button class="btn br bsm" onclick="fwForm(true)">+ Request documents</button></div>`)
      + (d.requests.length ? d.requests.map(requestCard).join('') : '<div class="emp">No requests yet.</div>');
  } else if (tab === 'entries') {
    await renderEntries(box, d);
  } else {
    renderCloseTab(box, d);
  }
  if (assignOpen) void renderAssign();
}

/* ── Etkileşimler ──────────────────────────────────────────────────── */

async function render(): Promise<void> {
  const root = el('fw-root');
  if (!root) return;
  const linkBox = el('fl-firm-box');
  if (linkBox && mode !== 'clients') linkBox.style.display = 'none';
  if (mode === 'client') { await renderClient(root); return; }
  if (!overview) { root.innerHTML = '<div class="emp">…</div>'; await loadOverview(); }
  if (mode === 'clients') renderClients(root);
  else if (mode === 'inbox') await renderInbox(root);
  else if (mode === 'missing') renderMissing(root);
  else renderClose(root);
}

let mounted = false;

function nav(m: unknown, id?: unknown, t?: unknown): void {
  mode = (['clients', 'inbox', 'missing', 'close', 'client'] as Mode[]).includes(m as Mode) ? m as Mode : 'clients';
  if (mode === 'client') {
    if (typeof id === 'string' && id !== linkId) { linkId = id; formOpen = false; assignOpen = false; tab = 'inbox'; }
    if ((['inbox', 'missing', 'entries', 'close'] as Tab[]).includes(t as Tab)) tab = t as Tab;
  }
  overview = null;
  /* Görünüm zaten açıksa kabuk mount'u tekrar çağırmaz: kendimiz çizeriz. */
  const wasMounted = mounted;
  legacy.navigate('v12', NAV[mode]);
  if (wasMounted) void render();
}

async function renderAssign(): Promise<void> {
  const box = el('fw-assign');
  if (!box || !detail) return;
  const team = await rpc<Array<{ member_id: string; role: string; status: string; name: string; email: string }>>('firm_team');
  const members = (team || []).filter((m) => m.role !== 'owner' && m.status === 'active');
  const on = new Set(detail.assigned.map((a) => a.member_id));
  box.innerHTML = `<div class="card" style="margin-bottom:12px"><div class="set">Who works on ${esc(detail.client_name)}? Only these people can open it.</div>`
    + (members.length ? members.map((m) =>
      `<label style="display:flex;gap:8px;align-items:center;padding:6px 0;text-transform:none;font-size:13px">`
      + `<input type="checkbox" ${on.has(m.member_id) ? 'checked' : ''} onchange="fwSetAssign('${esc(m.member_id)}',this.checked)">`
      + `${esc(m.name || m.email)} <span style="font-size:10px;color:var(--t2)">${esc(m.role)}</span></label>`).join('')
      : '<div style="font-size:12px;color:var(--t2)">Invite team members from Team first.</div>')
    + `<div style="font-size:10px;color:var(--t2);margin-top:6px">Managers always see every client.</div></div>`;
}

async function sendRequest(): Promise<void> {
  if (!detail) return;
  const v = (id: string) => (el(id) as HTMLInputElement | null)?.value ?? '';
  const items = draftItems.filter((i) => i.on).map((i) => ({ kind: i.kind, label: i.label.trim() }));
  const email = (el('fw-rq-email') as HTMLInputElement | null)?.checked === true;
  const r = await rpc<{ ok: boolean; error?: string; request_id?: string }>('create_document_request', {
    p_link_id: detail.link_id, p_title: v('fw-rq-title'), p_message: v('fw-rq-msg'), p_period: v('fw-rq-period'),
    p_due_date: v('fw-rq-due') || null, p_items: items, p_channels: email ? ['in_app', 'email'] : ['in_app'],
  });
  if (!r || !r.ok) { legacy.toast(r?.error ?? 'The request could not be sent.'); return; }
  let msg = 'Request sent. The client sees it on their Home page.';
  if (email && r.request_id) {
    try {
      const res = await legacy.postApi('/api/requests/notify', { request_id: r.request_id });
      msg = res.ok ? 'Request sent by email and in the app.' : 'Request sent in the app. The email could not be sent.';
    } catch { msg = 'Request sent in the app. The email could not be sent.'; }
  }
  legacy.toast(msg);
  formOpen = false;
  void render();
}

async function extract(link: string, documentId: string): Promise<void> {
  const d = detail && detail.link_id === link ? detail : await rpc<Detail>('firm_client_detail', { p_link_id: link });
  const doc = d?.documents.find((x) => x.id === documentId);
  if (!d || !d.ok || !doc) { legacy.toast('Document not found.'); return; }
  legacy.toast('Opening the document…');
  try {
    const res = await legacy.postApi('/api/files/open', { document_id: documentId, action: 'download' });
    const data = await res.json().catch(() => ({})) as { url?: string; error?: string };
    if (!res.ok || !data.url) { legacy.toast(data.error || 'The document could not be opened.'); return; }
    const blob = await (await fetch(data.url)).blob();
    const file = new File([blob], doc.name, { type: blob.type || 'application/octet-stream' });
    (globalThis as any)._pdfTarget = {
      profileId: d.profile_id, documentId, clientName: d.client_name, linkId: d.link_id,
      fileName: doc.name, until: Date.now() + 30 * 60 * 1000,
    };
    (globalThis as any)._pdfUsedAi = false;
    /* Talep edilen belge türü okumayı daraltır (ekstre: satırın yönü satırda; fatura: tek yön). */
    (globalThis as any).exPickKind?.(doc.request_kind || 'other');
    /* Okuma müşteri Belge sekmesine gitmez: bu müşterinin gelen kutusunda açılır. */
    extractFor = documentId;
    if (mode !== 'client' || linkId !== d.link_id || tab !== 'inbox') { mode = 'client'; linkId = d.link_id; tab = 'inbox'; }
    const root = el('fw-root');
    if (root) await renderClient(root);
    (globalThis as any).pdfProcess?.(file);
    const btn = el('pdf-import-btn');
    if (btn) btn.textContent = `✓ Write to ledger`;
  } catch {
    legacy.toast('The document could not be opened.');
  }
}

interface ReadRow { date: string; note: string; amount: number; type: string; sel?: boolean; dup?: boolean }

/** Aynı gün + tutar + yön + açıklama müşterinin defterinde varsa satır zaten yazılmıştır. */
export function markAlreadyInLedger(rows: ReadRow[], ledger: Array<{ date: string; amount: number; type: string; note: string | null }>): number {
  const key = (r: { date: string; amount: number; type: string; note: string | null }) =>
    [String(r.date).slice(0, 10), Number(r.amount).toFixed(2), r.type, String(r.note ?? '').trim().toLowerCase()].join(' ');
  const have = new Set(ledger.map(key));
  let n = 0;
  for (const r of rows) {
    if (have.has(key(r))) { r.dup = true; r.sel = false; n++; }
  }
  return n;
}

async function afterRead(rows: ReadRow[]): Promise<void> {
  const g = globalThis as any;
  const target = g._pdfTarget;
  if (!target || !extractFor || target.documentId !== extractFor || !el('fw-extract')) return;
  const tier = el('fw-ex-tier');
  if (tier) tier.innerHTML = g._pdfUsedAi
    ? '✦ Read by AI · check every line before writing'
    : 'Text layer found · read without AI · <b>0 credits</b>';
  const dates = rows.map((r) => String(r.date).slice(0, 10)).filter(Boolean).sort();
  let dups = 0;
  if (dates.length) {
    const r = await sb().from('transactions').select('date,amount,type,note')
      .eq('profile_id', target.profileId).gte('date', dates[0]).lte('date', dates[dates.length - 1]).limit(5000);
    dups = markAlreadyInLedger(rows, (r && !r.error && r.data) || []);
    if (dups) g.pdfRenderRows?.(rows);
  }
  const box = el('fw-ex-dups');
  if (box) box.innerHTML = dups
    ? `<div style="font-size:11px;color:#ff9800;margin-top:8px">! <b>${dups}</b> line${dups === 1 ? ' is' : 's are'} already in the ledger · unticked, will be skipped</div>` : '';
  const sign = el('fw-ex-sign');
  const who = String((legacy.user as { name?: string } | null)?.name ?? '').trim() || 'you';
  const firm = overview?.firm_name ?? g.navFirmContext?.()?.firm_name ?? 'your firm';
  if (sign) sign.textContent = `Signed as ${who} · ${firm}`;
  const btn = el('pdf-import-btn');
  const count = rows.filter((x) => x.sel).length;
  if (btn) btn.textContent = `Write ${count} to ledger`;
}

/** ai-files-ui.js pdfImport, hedef bir müşteri defteriyse buraya devreder. */
async function importRows(rows: Array<{ date: string; note: string; amount: number; type: string; cat: string; cur: string }>): Promise<boolean> {
  const g = globalThis as any;
  const target = g._pdfTarget;
  if (!target) return false;
  const payload = rows.filter((r) => r.amount > 0).map((r) => ({
    id: 'fw-' + crypto.randomUUID(), profile_id: target.profileId, type: r.type === 'income' ? 'income' : 'expense',
    category: r.cat || null, amount: r.amount, currency: r.cur || null, date: r.date, note: r.note || null,
  }));
  if (!payload.length) { legacy.toast('Select records to import.'); return true; }
  const ins = await sb().from('transactions').insert(payload);
  if (ins.error) { legacy.toast(String(ins.error.message ?? 'The entries could not be written.')); return true; }
  await rpc('set_document_review', { p_document_id: target.documentId, p_status: 'extracted', p_entries: payload.length });
  legacy.toast(`${payload.length} entries written to ${target.clientName}.`);
  g._pdfTarget = null;
  extractFor = null;
  restorePanel();
  const btn = el('pdf-import-btn');
  if (btn) btn.textContent = '✓ Import to System';
  nav('client', target.linkId, 'entries');
  return true;
}

export default defineFeature({
  id: 'firm-workspace',
  label: 'Ofis çalışma alanı',
  view: 'v12',

  mount: () => { mounted = true; void render(); },
  unmount: () => { mounted = false; restorePanel(); },

  legacyGlobals: {
    fwNav: (m: unknown, id?: unknown, t?: unknown) => nav(m, id, t),
    fwFilter: (f: unknown) => { filter = (['all', 'mine', 'waiting', 'ready'].includes(String(f)) ? f : 'all') as typeof filter; void render(); },
    fwSearch: (q: unknown) => {
      search = String(q ?? '');
      const root = el('fw-root');
      if (root && overview) {
        renderClients(root);
        const inp = root.querySelector<HTMLInputElement>('input[placeholder="Search clients…"]');
        if (inp) { inp.focus(); inp.setSelectionRange(search.length, search.length); }
      }
    },
    fwToggleLink: () => {
      const b = el('fl-firm-box');
      if (!b) return;
      b.style.display = b.style.display === 'none' ? '' : 'none';
      if (b.style.display === '') b.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    fwTab: (t: unknown) => { tab = t as Tab; void render(); },
    fwForm: (open: unknown) => { formOpen = open === true; void render(); },
    fwTemplate: (k: unknown) => { draftItems = (TEMPLATES[String(k)] ?? TEMPLATES.monthly).map((i) => ({ ...i, on: true })); void render(); },
    fwDraft: (i: unknown, key: unknown, value: unknown) => {
      const it = draftItems[Number(i)];
      if (!it) return;
      if (key === 'on') it.on = value === true;
      if (key === 'kind') it.kind = String(value);
      if (key === 'label') it.label = String(value).slice(0, 80);
    },
    fwDraftAdd: () => { draftItems.push({ kind: 'other', label: '', on: true }); void render(); },
    fwSendRequest: () => sendRequest(),
    fwCancelRequest: async (id: unknown) => {
      const r = await rpc<{ ok: boolean; error?: string }>('cancel_document_request', { p_request_id: String(id) });
      legacy.toast(r?.ok ? 'Request cancelled.' : (r?.error ?? 'The request could not be cancelled.'));
      void render();
    },
    fwReview: async (id: unknown, status: unknown) => {
      const r = await rpc<{ ok: boolean; error?: string }>('set_document_review', { p_document_id: String(id), p_status: String(status), p_entries: 0 });
      if (!r?.ok) legacy.toast(r?.error ?? 'Could not update the document.');
      overview = null;
      void render();
      void (globalThis as any).navRefreshBadges?.();
    },
    fwExtract: (link: unknown, id: unknown) => extract(String(link), String(id)),
    fwImportRows: (rows: unknown) => importRows(Array.isArray(rows) ? rows : []),
    fwExtractClose: () => {
      extractFor = null;
      (globalThis as any)._pdfTarget = null;
      restorePanel();
      const root = el('fw-root');
      if (root) void renderClient(root);
    },
    /** Okuma bitince (ai-files-ui pdfRender): kademe, deftere zaten yazılmış satırlar, imza. */
    fwAfterRead: (rows: unknown) => afterRead(Array.isArray(rows) ? rows : []),
    fwAssign: () => { assignOpen = !assignOpen; if (assignOpen) void renderAssign(); else { const b = el('fw-assign'); if (b) b.innerHTML = ''; } },
    fwSetAssign: async (member: unknown, on: unknown) => {
      if (!detail) return;
      const r = await rpc<{ ok: boolean; error?: string }>('set_client_assignment', { p_link_id: detail.link_id, p_member_id: String(member), p_assigned: on === true });
      if (!r?.ok) legacy.toast(r?.error ?? 'Could not update the assignment.');
      await loadDetail();
      const root = el('fw-root');
      if (root && detail) void renderClient(root);
    },
    fwAddEntry: async (cur: unknown) => {
      if (!detail) return;
      const v = (id: string) => (el(id) as HTMLInputElement | null)?.value ?? '';
      const amount = Number(v('fw-en-amount'));
      if (!(amount > 0) || !v('fw-en-date')) { legacy.toast('Enter a date and an amount.'); return; }
      const ins = await sb().from('transactions').insert({
        id: 'fw-' + crypto.randomUUID(), profile_id: detail.profile_id, type: v('fw-en-type') === 'income' ? 'income' : 'expense',
        amount, date: v('fw-en-date'), category: v('fw-en-cat') || null, note: v('fw-en-note') || null, currency: String(cur || '') || null,
      });
      legacy.toast(ins.error ? String(ins.error.message ?? 'The entry could not be added.') : 'Entry added.');
      void render();
    },
    fwEditEntry: async (id: unknown) => {
      const row = el('fw-en-' + String(id));
      if (!row || !detail) return;
      const r = await sb().from('transactions').select('id,date,amount,category,note').eq('id', String(id)).single();
      if (r.error || !r.data) return;
      const e = r.data as Entry;
      row.innerHTML = `<div style="display:flex;gap:6px;flex-wrap:wrap;width:100%">`
        + `<input class="inp" type="date" id="fw-ed-date" style="flex:0 0 150px;padding:6px" value="${esc(e.date)}">`
        + `<input class="inp" type="number" step="0.01" min="0" id="fw-ed-amount" style="flex:0 0 120px;padding:6px" value="${esc(e.amount)}">`
        + `<input class="inp" id="fw-ed-cat" style="flex:1 1 110px;padding:6px" value="${esc(e.category ?? '')}">`
        + `<input class="inp" id="fw-ed-note" style="flex:2 1 150px;padding:6px" value="${esc(e.note ?? '')}">`
        + `<button class="btn br bsm" onclick="fwSaveEntry('${esc(e.id)}')">Save</button></div>`;
    },
    fwSaveEntry: async (id: unknown) => {
      const v = (k: string) => (el(k) as HTMLInputElement | null)?.value ?? '';
      const amount = Number(v('fw-ed-amount'));
      if (!(amount > 0)) { legacy.toast('Enter an amount.'); return; }
      const up = await sb().from('transactions').update({
        date: v('fw-ed-date'), amount, category: v('fw-ed-cat') || null, note: v('fw-ed-note') || null,
      }).eq('id', String(id));
      legacy.toast(up.error ? String(up.error.message ?? 'The entry could not be saved.') : 'Entry corrected.');
      void render();
    },
    fwReload: () => { overview = null; void render(); },
  },

  owns: { tables: ['firm_document_reviews'] },
});
